/**
 * Offline Resilience Service
 * Gracefully handles offline scenarios and network failures
 * Uses API failure tracking (not just browser events) to determine connectivity
 */

import React, { useState, useEffect } from 'react';
import { getCacheManager } from './cache-manager';

class OfflineManager {
  constructor() {
    this.cacheManager = getCacheManager();
    this.isOnline = navigator.onLine;
    this.consecutiveFailures = 0;
    this.failureThreshold = 3; // Need 3 consecutive failures before going offline
    this.retryQueue = [];
    this.lastUpdated = new Map();
    this.listeners = new Set();

    console.log('[OfflineManager] Initialized. Initial State:', this.isOnline ? 'ONLINE' : 'OFFLINE');
    this.setupListeners();
  }

  setupListeners() {
    if (typeof window === 'undefined') return;

    window.addEventListener('online', () => {
      console.log('[OfflineManager] Browser Event: ONLINE');
      this.verifyConnectivity();
    });

    window.addEventListener('offline', () => {
      console.log('[OfflineManager] Browser Event: OFFLINE');
      this.handleOffline();
    });

    // Periodic check when offline to detect recovery
    this._connectivityInterval = setInterval(() => {
      if (!this.isOnline) {
        this.verifyConnectivity();
      }
    }, 10000);
  }

  async verifyConnectivity() {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081';
      const normalizedBaseUrl = apiBaseUrl.startsWith('http') ? apiBaseUrl : `https://${apiBaseUrl}`;

      const response = await fetch(`${normalizedBaseUrl}/api/health?ping=${Date.now()}`, {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal,
        credentials: 'include',
      });

      clearTimeout(timeoutId);

      if (response.ok || response.status < 500) {
        this.handleOnline();
      }
    } catch (e) {
      if (this.isOnline) {
        this.consecutiveFailures++;
        if (this.consecutiveFailures >= this.failureThreshold) {
          console.warn('[OfflineManager] Connectivity verification failed, marking offline');
          this.handleOffline();
        }
      }
    }
  }

  /**
   * Called by API interceptor on every successful response
   */
  reportSuccess() {
    this.consecutiveFailures = 0;
    if (!this.isOnline) {
      console.log('[OfflineManager] API Success: Restoring Online Status');
      this.handleOnline();
    }
  }

  /**
   * Called by API interceptor on network errors (no response at all)
   */
  reportFailure() {
    this.consecutiveFailures++;
    console.warn(`[OfflineManager] API Failure reported (${this.consecutiveFailures}/${this.failureThreshold})`);

    if (this.consecutiveFailures >= this.failureThreshold) {
      this.handleOffline();
    }
  }

  handleOffline() {
    if (this.isOnline === false) return;
    this.isOnline = false;
    console.error('[OfflineManager] SYSTEM STATUS: OFFLINE');
    this.listeners.forEach(l => {
      try { l.onOffline?.(); } catch (e) { console.error(e); }
    });
  }

  handleOnline() {
    if (this.isOnline === true) return;
    this.isOnline = true;
    this.consecutiveFailures = 0;
    console.log('[OfflineManager] SYSTEM STATUS: ONLINE');
    this.listeners.forEach(l => {
      try { l.onOnline?.(); } catch (e) { console.error(e); }
    });
  }

  /**
   * Track the last time an endpoint was successfully updated
   */
  trackUpdate(endpoint) {
    this.lastUpdated.set(endpoint, Date.now());
  }

  /**
   * Get cached fallback data for a URL when offline
   */
  async getCachedFallback(url, params = {}) {
    try {
      const cached = await this.cacheManager.get(url, params);
      if (cached.data) {
        const lastUpdate = this.lastUpdated.get(url);
        return {
          data: cached.data,
          lastUpdated: lastUpdate || null,
          source: cached.source,
          message: lastUpdate
            ? `Showing cached data from ${new Date(lastUpdate).toLocaleTimeString()}`
            : 'Showing cached data',
        };
      }
    } catch (e) {
      console.error('[OfflineManager] Failed to get cached fallback:', e);
    }
    return null;
  }

  notifyCacheMiss(url) {
    console.warn(`[OfflineManager] Cache Miss: ${url}`);
    this.listeners.forEach(l => {
      try { l.onCacheMiss?.(url); } catch (e) { console.error(e); }
    });
  }

  getIsOnline() {
    return this.isOnline;
  }

  /**
   * Get cache age for UI display
   */
  getCacheAge(endpoint) {
    const updated = this.lastUpdated.get(endpoint);
    if (!updated) return null;

    const ageMs = Date.now() - updated;
    const minutes = Math.floor(ageMs / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'just now';
  }

  /**
   * Get offline manager stats
   */
  getStats() {
    return {
      isOnline: this.isOnline,
      consecutiveFailures: this.consecutiveFailures,
      failureThreshold: this.failureThreshold,
      retryQueueLength: this.retryQueue.length,
      trackedEndpoints: this.lastUpdated.size,
    };
  }

  queueForRetry(url, params, fetchFn) {
    const retryItem = { url, params, fetchFn, timestamp: Date.now(), attempts: 0, maxAttempts: 3 };
    this.retryQueue.push(retryItem);
    return retryItem;
  }

  async processRetryQueue() {
    const queue = [...this.retryQueue];
    this.retryQueue = [];
    for (const item of queue) {
      try {
        item.attempts += 1;
        await item.fetchFn();
      } catch (error) {
        if (item.attempts < item.maxAttempts) {
          this.retryQueue.push(item);
        }
      }
    }
  }

  subscribe(listener) {
    this.listeners.add(listener);
    // Immediately notify of current state
    if (this.isOnline) listener.onOnline?.();
    else listener.onOffline?.();

    return () => this.listeners.delete(listener);
  }

  setCallbacks(callbacks) { this.subscribe(callbacks); }
}

// Global singleton with window backup to ensure single instance
let instance = null;
if (typeof window !== 'undefined') {
  if (!window.__OFFLINE_MANAGER__) {
    window.__OFFLINE_MANAGER__ = new OfflineManager();
  }
  instance = window.__OFFLINE_MANAGER__;
} else {
  instance = new OfflineManager();
}

export const getOfflineManager = () => instance;

/**
 * React Hook for offline status — reactive via subscription
 */
export const useOfflineStatus = () => {
  const manager = getOfflineManager();
  const [isOnline, setIsOnline] = useState(manager.getIsOnline());

  useEffect(() => {
    const unsubscribe = manager.subscribe({
      onOnline: () => setIsOnline(true),
      onOffline: () => setIsOnline(false),
    });
    return unsubscribe;
  }, [manager]);

  return { isOnline, manager };
};

export default OfflineManager;
